import { createContext, useContext } from 'react';

import type { ChatSession } from '@/hooks/useAgentChatSessions';

/** Which surface the popup body + header are showing. */
export type PopupView = 'chat' | 'history';

export type AgentAssistantHistory = {
  sessions: ChatSession[];
  isLoading: boolean;
  error?: Error | null;
  /** The thread currently shown in the chat, highlighted in the list. */
  currentThreadId?: string;
  /** Load a past conversation by its thread id. */
  onSelectThread: (threadId: string) => void;
  /** Start a fresh conversation. */
  onNewChat: () => void;
  /** Called when the history view is opened — drives lazy fetch / refetch. */
  onOpen?: () => void;
  /** Rename a past conversation. Rejects if the request fails. */
  onRenameThread?: (threadId: string, chatName: string) => Promise<void>;
  /** Delete a past conversation. Rejects if the request fails. */
  onDeleteThread?: (threadId: string) => Promise<void>;
};

type PopupViewContextValue = {
  view: PopupView;
  setView: (view: PopupView) => void;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  history?: AgentAssistantHistory;
};

const PopupViewContext = createContext<PopupViewContextValue | null>(null);

export const PopupViewProvider = PopupViewContext.Provider;

export const usePopupView = (): PopupViewContextValue => {
  const ctx = useContext(PopupViewContext);
  if (!ctx) {
    throw new Error('usePopupView must be used within a PopupViewProvider');
  }
  return ctx;
};
